import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'terms' | 'privacy' | null;
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al acceder y utilizar este producto digital ("Serenidad Nocturna"), aceptas estar sujeto a estos Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar este producto.'
  }, {
    heading: '2. Descripción del Producto',
    body: 'Serenidad Nocturna es un producto digital educativo que incluye un manual, videos demostrativos y material complementario sobre técnicas físicas para el alivio de cólicos en bebés. Este producto no constituye consejo médico profesional.'
  }, {
    heading: '3. Uso del Producto',
    body: 'El contenido de este producto es para uso personal y no comercial. Queda prohibida la reproducción, distribución, modificación o cualquier uso no autorizado del material sin consentimiento previo por escrito.'
  }, {
    heading: '4. Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto y su entrega inmediata, no se ofrecen reembolsos una vez completada la descarga. Si experimentas problemas técnicos con el acceso, contáctanos para resolverlo.'
  }, {
    heading: '5. Limitación de Responsabilidad',
    body: 'Las técnicas descritas en este producto son de carácter educativo. Los resultados pueden variar. No nos hacemos responsables por el uso inadecuado del material. Siempre consulta a un profesional de la salud ante cualquier duda sobre la salud de tu bebé.'
  }, {
    heading: '6. Propiedad Intelectual',
    body: 'Todo el contenido, incluyendo textos, imágenes, videos y diseños, es propiedad exclusiva de Serenidad Nocturna y está protegido por las leyes de propiedad intelectual aplicables.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.'
  }]
};
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito en nuestros servidores.'
  }, {
    heading: '2. Uso de la Información',
    body: 'Tu información personal se utiliza exclusivamente para: procesar tu pedido, enviarte el producto digital adquirido, y comunicarnos contigo en caso de ser necesario respecto a tu compra.'
  }, {
    heading: '3. Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción. Todos los pagos se procesan a través de plataformas seguras y encriptadas.'
  }, {
    heading: '4. Compartir Información',
    body: 'No vendemos, comercializamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios de pago necesarios para procesar tu transacción.'
  }, {
    heading: '5. Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.'
  }, {
    heading: '6. Tus Derechos',
    body: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. También puedes solicitar la portabilidad de tus datos o retirar tu consentimiento para el tratamiento de los mismos.'
  }, {
    heading: '7. Cambios en esta Política',
    body: 'Podemos actualizar esta política de privacidad periódicamente. Te notificaremos sobre cambios significativos a través del correo electrónico asociado a tu compra.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'terms' ? termsContent : privacyContent;
  return <>
      <footer className="py-12 bg-cream-dark border-t border-earth-light/30" data-id="element-42">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center" data-id="element-43">
          <p className="text-earth-dark text-sm mb-4" data-id="element-44">
            Este producto no sustituye el consejo médico profesional. Si tu bebé
            presenta síntomas inusuales, consulta a un pediatra.
          </p>
          <div className="flex justify-center gap-6 text-sm text-earth-dark/70" data-id="element-45">
            <button onClick={() => setActiveModal('terms')} className="hover:text-accent transition-colors cursor-pointer" data-id="element-46">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-accent transition-colors cursor-pointer" data-id="element-47">
              Política de Privacidad
            </button>
          </div>
          <p className="text-earth-dark/50 text-xs mt-8" data-id="element-48">
            © {new Date().getFullYear()} Serenidad Nocturna. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-49">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-earth-darker/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-50">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-51">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-earth-light/30 px-6 md:px-8 py-5 flex items-center justify-between z-10" data-id="element-52">
                <h2 className="text-2xl font-bold font-serif text-earth-darker" data-id="element-53">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-cream-dark flex items-center justify-center hover:bg-earth-light transition-colors" aria-label="Cerrar" data-id="element-54">
                  <X className="w-5 h-5 text-earth-darker" data-id="element-55" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 md:px-8 py-8 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-56">
                <div className="space-y-8" data-id="element-57">
                  {content.sections.map((section, idx) => <div key={idx} data-id="element-58">
                      <h3 className="text-lg font-bold text-earth-darker mb-2" data-id="element-59">
                        {section.heading}
                      </h3>
                      <p className="text-earth-dark leading-relaxed" data-id="element-60">
                        {section.body}
                      </p>
                    </div>)}
                </div>

                <div className="mt-10 pt-6 border-t border-earth-light/30 text-center" data-id="element-61">
                  <p className="text-sm text-earth-dark/60" data-id="element-62">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-ES', {
                  month: 'long',
                  year: 'numeric'
                })}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}